import { Fragment } from "react";
import { toast } from "react-toastify";
import CustomModal from "../components/Modal/CustomModal";

const GATE_TOAST_OPTIONS = {
  toastId: "customId",
  transition: undefined,
  autoClose: false,
  closeOnClick: false,
  draggable: false,
  closeButton: false,
  hideProgressBar: true,
};

function toMultilineNode(text) {
  const lines = String(text).split("\n");
  return lines.map((line, i) => (
    <Fragment key={i}>
      {line}
      {i < lines.length - 1 && <br />}
    </Fragment>
  ));
}


export const errorHandler = (error, text, navigate) => {
  let errorMessage = "";

  if (typeof error === "object") {
    const responseMessage = error;
    errorMessage = Object.keys(responseMessage)
      .map((key) => `${responseMessage[key]}`)
      .join("\n");
  } else if (typeof error === "string") {
    errorMessage = error;
  } else {
    errorMessage =
      "Something went wrong, Please check your internet connection and try again.";
  }

  toast(
    ({ closeToast }) => (
      <CustomModal
        state="failed"
        title="Something went wrong"
        description={toMultilineNode(errorMessage)}
        primaryLabel="Dismiss"
        secondaryLabel="Contact Support"
        onClose={closeToast}
        onPrimaryAction={() => {
          closeToast();
          if (typeof navigate === "function") navigate();
        }}
      />
    ),
    GATE_TOAST_OPTIONS,
  );
};

// Plan limits (403 "…upgrade your plan…"), a missing plan and too few credits
// for a match get a way to buy more instead of a dead-end error.
export const isPlanLimitError = (error) => {
  const status = error?.response?.status;
  const errors = error?.response?.data?.errors;
  if (typeof errors === "string") {
    if (status === 403 && /upgrade/i.test(errors)) return true;
    if (status === 400 && /active quota/i.test(errors)) return true;
  }
  return status === 400 && typeof errors === "object" && !!errors?.credits;
};

export const planLimitHandler = (error, navigate) => {
  const errors = error?.response?.data?.errors;
  const isQuota = typeof errors === "string" && /active quota/i.test(errors);
  const isCredits = typeof errors === "object" && !!errors?.credits;
  const message = isCredits ? errors.credits : errors;

  toast(
    ({ closeToast }) => (
      <CustomModal
        state="failed"
        title={
          isQuota
            ? "No active plan"
            : isCredits
              ? "Not enough credits"
              : "Plan limit reached"
        }
        description={toMultilineNode(message)}
        primaryLabel={isCredits ? "Buy credits" : "View plans"}
        secondaryLabel="Contact Support"
        onClose={closeToast}
        onPrimaryAction={() => {
          closeToast();
          if (typeof navigate === "function") navigate("/subscription-payment");
        }}
      />
    ),
    GATE_TOAST_OPTIONS,
  );
};

export const formatCredits = (n) => {
  const value = Number(n);
  if (!Number.isFinite(value)) return "";
  return `${value} credit${value === 1 ? "" : "s"}`;
};

// The quote sits on res.data.data. A wrapped payload still counts, so a
// successful response is not thrown away because one extra data key is present.
export const quoteFromResponse = (res) => {
  const body = res?.data;
  if (!body?.success) return null;
  const payload = body.data;
  if (payload && payload.cost != null) return payload;
  if (payload?.data && payload.data.cost != null) return payload.data;
  return null;
};

export const quoteErrorText = (error) => {
  const errors = error?.response?.data?.errors;
  if (typeof errors === "string" && errors.trim()) return errors;
  if (errors && typeof errors === "object") {
    const message = Object.values(errors).find(
      (value) => typeof value === "string" && value.trim(),
    );
    if (message) return message;
  }
  return "We couldn't load the price for this match.";
};

// Mirrors the match charge: a free trial, then an unused free cancel, then a
// rematch guarantee, then the role price. A quote with a cost but no separate
// full price still describes the cost, so the disclaimer is not left blank.
export const describeMatchCost = (quote) => {
  if (!quote || quote.cost == null || !Number.isFinite(Number(quote.cost))) return "";
  const full = formatCredits(quote.fullCost == null ? quote.cost : quote.fullCost);
  const price = formatCredits(quote.cost);
  const left = Number.isFinite(Number(quote.creditsLeft))
    ? ` You have ${formatCredits(quote.creditsLeft)} left.`
    : "";

  if (quote.reason === "free_trial") {
    return `This match is free on your trial. This role normally costs ${full}.`;
  }
  if (quote.reason === "free_rematch") {
    return `This match is free because you cancelled a match recently. This role normally costs ${full}.`;
  }
  if (quote.reason === "rematch_guarantee") {
    return `This match uses your rematch guarantee and costs ${price} instead of ${full}.${left}`;
  }
  return `This match costs ${price}.${left}`;
};

export const toastSuccess = (text, navigate) => {
  toast(
    ({ closeToast }) => (
      <CustomModal
        state="success"
        title="Success"
        description={text}
        primaryLabel="Continue"
        onClose={closeToast}
        onPrimaryAction={() => {
          closeToast();
          if (typeof navigate === "function") navigate();
        }}
      />
    ),
    GATE_TOAST_OPTIONS,
  );
};
